// MARK:- OS 2.0 storefront-wide add-to-cart delegator. Lets non-PDP buttons
// (product cards, list items, spotlight CTAs) post to /cart/add.js and trigger
// the cart drawer via the existing cart:added event. Document-level delegation
// — no custom element wrapping legacy markup.
//
// File naming: hand-edited asset, NOT a webpack/gulp build output. Loaded
// per-section via:
//
//   <script src="{{ 'component-add-to-cart.js' | asset_url }}" type="module"></script>
//
// Markup contract:
//
//   <form action="/cart/add" method="post" enctype="multipart/form-data" data-as-add-to-cart>
//     <input type="hidden" name="id" value="{{ variant.id }}">
//     <input type="hidden" name="quantity" value="1">
//     <button type="submit">+ Add to basket</button>
//   </form>
//
// Behavior:
// - Submit posts FormData to /cart/add.js (Shopify Cart AJAX API).
// - On success, dispatches `cart:added` (component-cart.js refreshes the drawer
//   in place via /cart/update.js?sections=cart-drawer) and clicks the cart
//   drawer trigger to open it (matches component-product-form.js flow).
// - Submit button is disabled during the request to avoid double-adds.
// - Idempotent: window.__asAddToCartInit guard prevents double-binding when the
//   script is loaded from multiple sections.

(() => {
  if (window.__asAddToCartInit) return;
  window.__asAddToCartInit = true;

  // MARK:- Shopify's hard-coded cart-cap strings. Both fire when a customer hits the per-line limit (one for "added < requested", one for "already at max"). Substitute settings.cart_quantity_max_message (read off <body data-as-qty-message>) when either matches.
  const SHOPIFY_LIMIT_RE = /(?:Only (\d+) items? were added to your cart due to availability|The maximum quantity of this item is already in your cart)\./i;

  function openCartDrawer() {
    const drawer = document.querySelector('#cart.drawer');
    if (!drawer) return;
    const trigger = document.querySelector('[data-as-drawer="cart"]:not([data-as-drawer-hover])');
    if (trigger && !drawer.classList.contains('is-open')) trigger.click();
  }

  // MARK:- Surface card/spotlight add-to-cart failures (previously silent) via the cart-drawer error region. Drawer is opened so the customer sees the message — there is no per-card error UI today.
  function showCartDrawerError(rawMessage) {
    const target = document.querySelector('[data-as-cart-error]');
    if (!target) return;
    const themeMessage = document.body.dataset.asQtyMessage || '';
    const display = (themeMessage && SHOPIFY_LIMIT_RE.test(rawMessage)) ? themeMessage : rawMessage;
    target.textContent = display || '';
    target.hidden = !display;
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-as-add-to-cart]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const formData = new FormData(form);

      // MARK:- Route through Shopify.actions.updateCart when our configured
      // handler won registration (snippets/storefront-actions.liquid) — the
      // handler runs the same one-round-trip pipeline in component-cart.js and
      // the standard shopify:cart:* events auto-emit for app listeners. Forms
      // carrying properties/selling plans can't be expressed in the updateCart
      // payload and keep the direct path below.
      const canRoute = window.__asActionsConfigured && window.__asActionsConfigured.updateCart
        && window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.updateCart === 'function';
      const hasExtras = [...formData.keys()].some((k) => k.startsWith('properties[') || k === 'selling_plan');
      const routedId = formData.get('id');
      if (canRoute && !hasExtras && routedId) {
        const payload = { lines: [{ merchandiseId: String(routedId), quantity: parseInt(formData.get('quantity'), 10) || 1 }] };
        const runsBefore = window.__asCartHandlerRuns ? window.__asCartHandlerRuns() : 0;
        let routedResult = null;
        let routedFailed = false;
        try {
          routedResult = await window.Shopify.actions.updateCart(
            payload,
            { event: { context: 'product', detail: { source: 'card-add-to-cart' } } },
          );
        } catch (actionErr) {
          // MARK:- A rejection AFTER our handler ran means the mutation applied and the actions runtime failed post-hoc — sync the UI, never show the raw runtime error. A pre-handler rejection means nothing happened; fall through to the direct path.
          console.error('[as-add-to-cart] updateCart action', actionErr);
          const handled = (window.__asCartPayloadHandled && window.__asCartPayloadHandled(payload))
            || (window.__asCartHandlerRuns && window.__asCartHandlerRuns() > runsBefore);
          if (handled) {
            openCartDrawer();
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }
          routedFailed = true;
        }
        if (!routedFailed) {
          openCartDrawer();
          if (routedResult && routedResult.userErrors && routedResult.userErrors.length) {
            // MARK:- Handler already surfaced the error in the drawer; limit-hits still mean the cart has items, so re-sync what the customer sees.
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
          return;
        }
      }

      // MARK:- Request the cart-drawer section render in the same round trip so
      // component-cart.js can apply it without a follow-up /cart/update.js fetch.
      formData.append('sections', 'cart-drawer');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Could not add to basket.');
      }
      const response = await res.json();
      // MARK:- Successful add clears any stale cart-drawer error from a prior limit-hit attempt.
      const errEl = document.querySelector('[data-as-cart-error]');
      if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
      // Dispatch BEFORE opening: component-cart.js applies the section synchronously
      // so the drawer opens with fresh contents already in place.
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { response } }));
      openCartDrawer();
    } catch (err) {
      console.error('[as-add-to-cart]', err);
      showCartDrawerError((err && err.message) || '');
      openCartDrawer();
      // MARK:- Limit-hit responses still mean the cart already has items; refresh the drawer state so what the customer sees matches reality.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      if (button) button.disabled = false;
    }
  });
})();
